import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Check, Search } from "lucide-react";

import ArtistAvatar from "../Components/ArtistAvatar";
import { useAuth } from "../Services/AuthContext";
import {
  fetchFavoriteArtists,
  fetchPopularArtists,
  saveFavoriteArtists,
  searchArtists,
} from "../Services/SocialService";
import type { ArtistHit } from "../data/types";
import "../Styles/OnboardingArtists.css";

const MAX_PICKS = 5;

/**
 * Första steget efter registrering: välj upp till fem toppartister.
 *
 * Går att hoppa över. Att tvinga fram ett val innan man ens sett appen är ett
 * säkert sätt att tappa folk, och favoriterna går att sätta när som helst
 * från profilen.
 */
export default function OnboardingArtists() {
  const { isAuthenticated, isLoading: isAuthLoading } = useAuth();
  const navigate = useNavigate();

  const [suggestions, setSuggestions] = useState<ArtistHit[]>([]);
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<ArtistHit[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const [picked, setPicked] = useState<ArtistHit[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sidan hör till registreringen. Har man redan valt är den avklarad, och
  // då ska den inte dyka upp igen om man råkar navigera hit.
  const [alreadyDone, setAlreadyDone] = useState<boolean | null>(null);

  useEffect(() => {
    if (!isAuthenticated) return;

    let alive = true;

    fetchFavoriteArtists()
      .then((res) => {
        if (alive) setAlreadyDone(res.items.length > 0);
      })
      .catch(() => {
        if (alive) setAlreadyDone(false);
      });

    fetchPopularArtists(24)
      .then((items) => {
        if (alive) setSuggestions(items);
      })
      .catch(() => {
        if (alive) setSuggestions([]);
      });

    return () => {
      alive = false;
    };
  }, [isAuthenticated]);

  useEffect(() => {
    const trimmed = q.trim();

    if (trimmed.length < 2) {
      setHits([]);
      return;
    }

    let alive = true;
    setIsSearching(true);

    const timer = window.setTimeout(async () => {
      const result = await searchArtists(trimmed).catch(() => [] as ArtistHit[]);

      if (!alive) return;

      setHits(result);
      setIsSearching(false);
    }, 250);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [q]);

  const toggle = useCallback((artist: ArtistHit) => {
    setError(null);

    setPicked((current) => {
      if (current.some((a) => a.id === artist.id)) {
        return current.filter((a) => a.id !== artist.id);
      }

      if (current.length >= MAX_PICKS) return current;

      return [...current, artist];
    });
  }, []);

  const finish = async (artistIds: string[]) => {
    setIsSaving(true);
    setError(null);

    try {
      await saveFavoriteArtists(artistIds);
      navigate("/", { replace: true });
    } catch (e) {
      console.error("onboarding save failed", e);
      setError("Could not save your artists. Try again.");
      setIsSaving(false);
    }
  };

  if (!isAuthLoading && !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (alreadyDone) {
    return <Navigate to="/" replace />;
  }

  const isPicked = (artist: ArtistHit) => picked.some((a) => a.id === artist.id);
  const isFull = picked.length >= MAX_PICKS;

  // Söker man visas träffarna, annars förslagen.
  const shown = q.trim().length >= 2 ? hits : suggestions;

  return (
    <main className="onboarding">
      <div className="onboarding-inner">
        <header className="onboarding-header">
          <p className="onboarding-eyebrow">Welcome to Beatspot</p>
          <h1>Who are you listening to?</h1>
          <span>
            Pick up to {MAX_PICKS} artists. We use them to show you which
            places are playing your kind of music.
          </span>
        </header>

        <div className="onboarding-picked" aria-live="polite">
          {Array.from({ length: MAX_PICKS }).map((_, index) => {
            const artist = picked[index];

            return artist ? (
              <button
                key={artist.id}
                type="button"
                className="onboarding-slot onboarding-slot--filled"
                onClick={() => toggle(artist)}
                aria-label={`Remove ${artist.name}`}
              >
                <ArtistAvatar name={artist.name} seed={artist.id} size={44} />
                <span>{artist.name}</span>
              </button>
            ) : (
              <div key={`empty-${index}`} className="onboarding-slot">
                <div className="onboarding-slot-empty" />
                <span>{index + 1}</span>
              </div>
            );
          })}
        </div>

        <div className="onboarding-search">
          <Search size={16} strokeWidth={2.2} />
          <input
            placeholder="Search for an artist..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {error && <p className="onboarding-error">{error}</p>}

        <p className="onboarding-label">
          {q.trim().length >= 2
            ? isSearching && hits.length === 0
              ? "Searching…"
              : `Results for “${q.trim()}”`
            : "Popular right now"}
        </p>

        {shown.length === 0 ? (
          <p className="onboarding-empty">
            {q.trim().length >= 2
              ? "No artists found in the Beatspot catalogue."
              : "The catalogue is empty. Run the catalogue import to fill it."}
          </p>
        ) : (
          <div className="onboarding-grid">
            {shown.map((artist) => {
              const selected = isPicked(artist);

              return (
                <button
                  key={artist.id}
                  type="button"
                  className={`onboarding-card ${selected ? "is-selected" : ""}`}
                  // Full lista låser bara de som inte redan är valda, så man
                  // fortfarande kan klicka bort någon.
                  disabled={isFull && !selected}
                  onClick={() => toggle(artist)}
                >
                  <ArtistAvatar name={artist.name} seed={artist.id} size={56} />
                  <span className="onboarding-card-name">{artist.name}</span>

                  {selected && (
                    <span className="onboarding-card-check">
                      <Check size={12} strokeWidth={3} />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}

        <div className="onboarding-actions">
          <button
            type="button"
            className="onboarding-continue"
            disabled={picked.length === 0 || isSaving}
            onClick={() => finish(picked.map((a) => a.id))}
          >
            {isSaving
              ? "Saving…"
              : picked.length
              ? `Continue with ${picked.length}`
              : "Pick at least one"}
          </button>

          <button
            type="button"
            className="onboarding-skip"
            disabled={isSaving}
            onClick={() => navigate("/", { replace: true })}
          >
            Skip for now
          </button>
        </div>
      </div>
    </main>
  );
}
